import {
  CheckIcon,
  DotsThreeVertical,
  Lock,
  PencilSimple,
  Trash,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { cn } from '@/lib/utils';
import type { CustomFieldTableRow, CustomFieldTier } from '../../utils/custom-fields-table-rows';

export type CustomFieldsTableColumnsOptions = {
  onEdit: (row: CustomFieldTableRow) => void;
  onDelete: (row: CustomFieldTableRow) => void;
};

const TIER_LABEL: Record<CustomFieldTier, string> = {
  native: 'Native',
  default: 'Default',
  custom: 'Custom',
};

const TIER_CLASS: Record<CustomFieldTier, string> = {
  native: 'border border-border bg-background text-muted-foreground',
  default: 'border border-transparent bg-[#0161AB]/15 text-[#0161AB]',
  custom: 'border border-transparent bg-violet-500/15 text-violet-700 dark:text-violet-300',
};

const VISIBLE_CHOICES = 3;

export const CustomFieldLabelCell = ({ row }: { row: CustomFieldTableRow }) => (
  <div className="flex min-w-0 items-center gap-2">
    <span className="truncate font-medium text-foreground">{row.label}</span>
    <span
      className={cn(
        'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        TIER_CLASS[row.tier]
      )}
    >
      {TIER_LABEL[row.tier]}
    </span>
  </div>
);

const impliedChoices = (dataTypeLabel: string, choices: string[]): string[] => {
  if (choices.length > 0) return choices;
  if (dataTypeLabel === 'Boolean') return ['True', 'False'];
  return [];
};

export const CustomFieldChoicesCell = ({
  choices,
  dataTypeLabel,
}: {
  choices: string[];
  dataTypeLabel: string;
}) => {
  const resolved = impliedChoices(dataTypeLabel, choices);

  if (resolved.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const visible = resolved.slice(0, VISIBLE_CHOICES);
  const extra = resolved.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((choice, index) => (
        <span
          key={`${choice}-${index}`}
          className="inline-flex max-w-[8.5rem] truncate rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
          title={choice}
        >
          {choice}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs text-muted-foreground" title={resolved.join(', ')}>
          +{extra}
        </span>
      )}
    </div>
  );
};

export const CustomFieldFilterableCell = ({ isFilterable }: { isFilterable: boolean }) =>
  isFilterable ? (
    <span className="inline-flex items-center" aria-label="Filterable">
      <CheckIcon className="h-4 w-4 text-emerald-600" />
    </span>
  ) : (
    <span className="inline-flex items-center" aria-label="Not filterable">
      <XIcon className="h-4 w-4 text-muted-foreground" />
    </span>
  );

export const CustomFieldActionsCell = ({
  row,
  onEdit,
  onDelete,
}: {
  row: CustomFieldTableRow;
} & CustomFieldsTableColumnsOptions) => {
  if (row.tier === 'native') {
    return (
      <span
        className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground/70"
        aria-label="Native field is read-only"
      >
        <Lock className="h-3.5 w-3.5" aria-hidden />
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
          aria-label={`Actions for ${row.label}`}
        >
          <DotsThreeVertical className="size-[18px]" weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem] rounded-lg border-neutral-200 p-1.5 shadow-e3">
        <DropdownMenuItem className="gap-2.5" onSelect={() => onEdit(row)}>
          <PencilSimple className="size-4" aria-hidden />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => onDelete(row)}
          className="gap-2.5 text-error focus:bg-error-background focus:text-error"
        >
          <Trash className="size-4" aria-hidden />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
